/**
 * Call recording player — slim inline transport for the acqs activity feed.
 *
 * Each `.js-audio` element is a play button + progress track + time label
 * (markup in templates/acqs/_log_entry.html). A single shared <audio> element
 * is reused across all players, so only one recording ever plays at a time and
 * we never preload audio until the rep actually hits play. Event-delegated, so
 * players added by HTMX swaps (activity card refresh) work with no re-init.
 *
 * A swap can also REPLACE the player that is playing (the Inbound Calls worklist
 * re-renders a whole row after a link or dismiss). `rebind()` then finds the new
 * element by its `data-audio-src` and moves the transport onto it, so the progress
 * bar keeps moving instead of freezing on a detached node.
 */
(function () {
  var audio = null;
  var active = null; // the .js-audio element currently bound to <audio>
  var refreshed = false; // whether we already re-minted the URL for this playback
  var resumeAt = 0; // playback position to restore after a re-mint

  function fmt(seconds) {
    if (!seconds || isNaN(seconds) || seconds < 0) return '0:00';
    var m = Math.floor(seconds / 60);
    var s = Math.floor(seconds % 60);
    return m + ':' + (s < 10 ? '0' + s : s);
  }

  function getAudio() {
    if (audio) return audio;
    audio = new Audio();
    audio.preload = 'none';
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('play', syncIcons);
    audio.addEventListener('pause', syncIcons);
    audio.addEventListener('error', onError);
    // Audio actually reaching the speakers clears the retry guard, so a later
    // expiry in the same sitting gets its own single refresh.
    audio.addEventListener('playing', function () { refreshed = false; });
    return audio;
  }

  // Restores the pre-error position on the re-minted source. Named (not inline)
  // so it can be un-registered: if the re-minted source ALSO fails,
  // `loadedmetadata` never fires and a `{once:true}` listener would sit armed
  // until the *next* recording loads — then seek that one to the previous
  // recording's position (or past its end, which fires `ended` immediately).
  function onResumeMeta() {
    audio.currentTime = resumeAt;
    audio.play();
  }

  function cancelResume() {
    if (audio) audio.removeEventListener('loadedmetadata', onResumeMeta);
  }

  // The signed recording URL expires (15 min), so a player paused past that —
  // or scrubbed after sitting open — 403s on resume and would otherwise stall
  // silently. `data-audio-src` points at the Django view, which mints a fresh
  // signed URL and 302s on every request, so simply re-assigning it is the
  // whole refresh. Once per playback only: a genuinely broken recording would
  // fail again and loop forever.
  function onError() {
    if (!active || refreshed) return;
    refreshed = true;
    resumeAt = audio.currentTime;
    audio.src = active.getAttribute('data-audio-src');
    // currentTime is only settable once the new source reports its duration.
    cancelResume();
    audio.addEventListener('loadedmetadata', onResumeMeta, { once: true });
    audio.load();
  }

  function icon(player, playing) {
    var i = player.querySelector('.js-audio-toggle i');
    if (i) i.className = 'fa-solid ' + (playing ? 'fa-pause' : 'fa-play');
  }

  function syncIcons() {
    document.querySelectorAll('.js-audio').forEach(function (p) {
      icon(p, p === active && audio && !audio.paused);
    });
  }

  // If `active` was swapped out of the page, point it at the element that replaced
  // it — same `data-audio-src` — and carry the playback speed and progress across.
  function rebind() {
    if (!active || active.isConnected) return;
    var src = active.getAttribute('data-audio-src');
    var rate = active.getAttribute('data-audio-rate');
    var next = null;
    document.querySelectorAll('.js-audio').forEach(function (p) {
      if (!next && p.getAttribute('data-audio-src') === src) next = p;
    });
    active = next;
    if (!active) return;
    if (rate) {
      active.setAttribute('data-audio-rate', rate);
      active.querySelectorAll('.js-audio-speed').forEach(function (el) {
        el.classList.toggle('active', parseFloat(el.getAttribute('data-rate')) === parseFloat(rate));
      });
    }
    onTime();
    syncIcons();
  }

  function onTime() {
    // Also caught here, not only on afterSettle: an out-of-band swap may not settle
    // on an element whose afterSettle reaches <body>.
    if (active && !active.isConnected) return rebind();
    if (!active || !audio.duration) return;
    var pct = (audio.currentTime / audio.duration) * 100;
    var fill = active.querySelector('.js-audio-fill');
    var time = active.querySelector('.js-audio-time');
    if (fill) fill.style.width = pct + '%';
    if (time) time.textContent = fmt(audio.currentTime);
  }

  function onEnded() {
    if (!active) return;
    var fill = active.querySelector('.js-audio-fill');
    var time = active.querySelector('.js-audio-time');
    if (fill) fill.style.width = '0';
    if (time) time.textContent = fmt(active.getAttribute('data-audio-duration'));
    syncIcons();
  }

  function toggle(player) {
    var a = getAudio();
    if (active === player) {
      a.paused ? a.play() : a.pause();
      return;
    }
    // Switch to a different recording.
    if (active) {
      var prevFill = active.querySelector('.js-audio-fill');
      if (prevFill) prevFill.style.width = '0';
    }
    active = player;
    refreshed = false;
    cancelResume();
    a.src = player.getAttribute('data-audio-src');
    a.playbackRate = parseFloat(player.getAttribute('data-audio-rate')) || 1;
    a.play();
  }

  function setSpeed(item) {
    var player = item.closest('.js-audio');
    var rate = parseFloat(item.getAttribute('data-rate')) || 1;
    player.setAttribute('data-audio-rate', rate);
    player.querySelectorAll('.js-audio-speed').forEach(function (el) {
      el.classList.toggle('active', el === item);
    });
    if (active === player && audio) audio.playbackRate = rate;
  }

  // Initialise the time label to the recording's known duration.
  function paint() {
    document.querySelectorAll('.js-audio').forEach(function (p) {
      var time = p.querySelector('.js-audio-time');
      if (time && p !== active) {
        time.textContent = fmt(parseInt(p.getAttribute('data-audio-duration'), 10));
      }
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.js-audio-toggle');
    if (btn) {
      e.preventDefault();
      toggle(btn.closest('.js-audio'));
      return;
    }
    var speed = e.target.closest('.js-audio-speed');
    if (speed) {
      e.preventDefault();
      setSpeed(speed);
      return;
    }
    var track = e.target.closest('.js-audio-track');
    if (track && audio && active === track.closest('.js-audio') && audio.duration) {
      var rect = track.getBoundingClientRect();
      audio.currentTime = ((e.clientX - rect.left) / rect.width) * audio.duration;
    }
  });

  document.addEventListener('DOMContentLoaded', paint);
  document.body.addEventListener('htmx:afterSettle', function () {
    rebind();
    paint();
  });
})();
